// skipLogic.js

/**
 * Evaluate skip/unskip logic for each question based on user answers & factor definitions.
 * 
 * @param {Array} questionList - The full array of question objects.
 * @param {Object} answers - Map of { questionId: userAnswer }
 * @param {Array} factors - (Optional) list of factor definitions
 * @param {Array} aggregateFactors - (Optional) for aggregate logic
 * @returns {Array} A new array with updated `.skip = true/false`.
 */
export function applyQuestionVisibilityLogic(
    questionList,
    answers,
    factors = [],
    aggregateFactors = []
  ) {
    // Precompute factor averages or whatever data you need once here:
    const factorAverages = computeAllFactorAverages(questionList, answers, factors);
    // If you need to figure out the highest-scoring factor, do that once:
    const highestFactorId = getHighestFactorId(factorAverages);
  
    // Evaluate each question’s skip logic:
    return questionList.map((question) => {
      // Determine if this is a followup (or factor/aggregate followup)
      const isFollowup = ["followup", "factorFollowup", "aggregateFactorFollowup"].includes(question.type);
      // Start with the provided default skip value
      let skip = !!question.skip;
  
      // Process skipConditions (if any)
      if (Array.isArray(question.skipConditions) && question.skipConditions.length > 0) {
        for (const rule of question.skipConditions) {
          const conditionMet = evaluateCondition(rule, answers, questionList, factorAverages, highestFactorId);
          if (isFollowup) {
            // For followup questions, if any skipCondition is met, we "unskip" (show them)
            if (conditionMet === true) {
              skip = false;
              break;
            }
          } else {
            // For regular questions, if any skipCondition is met, we skip (hide them)
            if (conditionMet === true) {
              skip = true;
              break;
            }
          }
        }
      }
  
      // Process unskipCondition if present.
      // This is common for followups that are skipped by default.
      if (question.unskipCondition) {
        const unskipMet = evaluateCondition(
          question.unskipCondition,
          answers,
          questionList,
          factorAverages,
          highestFactorId
        );
        if (unskipMet === true) {
          skip = false;
        }
      }
  
      return { ...question, skip };
    });
  }
  
  /**
   * Evaluate a single condition object. The condition might be:
   *   1) A “basic” numeric/string logic:
   *        { previousQuestion, operator, threshold }
   *   2) A factor-based condition:
   *        { conditionType: "factorAverage", factorId, operator, threshold }
   *   3) An aggregate condition:
   *        { conditionType: "highestScoringFactor", factorId? }
   */
  function evaluateCondition(rule, answers, questionList, factorAverages, highestFactorId) {
    // If the rule has a "conditionType", we handle factor/aggregate logic:
    if (rule.conditionType) {
      switch (rule.conditionType) {
        case "factorAverage":
          return evaluateFactorAverageCondition(rule, factorAverages);
        case "highestScoringFactor":
          return evaluateHighestFactorCondition(rule, highestFactorId);
        // You could handle "lowestScoringFactor" similarly, etc.
        default:
          return false;
      }
    }
  
    // Otherwise, do the old basic numeric/array logic.
    return evaluateBasicCondition(rule, answers);
  }
  
  /**
   * For factorAverage: 
   *   rule = {
   *     conditionType: "factorAverage",
   *     factorId: "factor_123",
   *     operator: ">",
   *     threshold: 7
   *   }
   */
  function evaluateFactorAverageCondition(rule, factorAverages) {
    const { factorId, operator, threshold } = rule;
    if (!factorId) return false;
  
    const factorObj = factorAverages.find((fa) => fa.factorId === factorId);
    if (!factorObj) return false; // no factor found
  
    const avg = factorObj.avg || 0;
    return compareNumbers(avg, operator, threshold);
  }
  
  /**
   * For highestScoringFactor:
   *   rule = { conditionType: "highestScoringFactor", factorId?: ... }
   * If no factorId is provided, we assume the condition is met as long as a highest factor exists.
   */
  function evaluateHighestFactorCondition(rule, highestFactorId) {
    const { factorId } = rule;
    if (!factorId) {
      // If no factorId is provided, return true if there is a highest-scoring factor.
      return highestFactorId !== null;
    }
    return factorId === highestFactorId;
  }
  
  /**
   * Old logic for numeric or array-based skip:
   *   { previousQuestion, operator, threshold }
   * Updated to also check for "parentQuestionId" if "previousQuestion" is not present.
   */
  function evaluateBasicCondition(rule, answers) {
    const questionId = rule.previousQuestion || rule.parentQuestionId;
    if (!questionId) return false;
    const answerObj = answers[questionId];
    if (answerObj == null) return false;
    
    // Use the 'response' value if available
    const userVal = typeof answerObj === 'object' && answerObj.response !== undefined
      ? answerObj.response
      : answerObj;
    
    if (userVal == null) return false;
    
    const valNum = parseFloat(userVal);
    const thNum = parseFloat(rule.threshold);
    if (isNaN(valNum) || isNaN(thNum)) return false;
    
    switch (rule.operator) {
      case "gt":
        return valNum > thNum;
      case "lt":
        return valNum < thNum;
      case "eq":
        return valNum === thNum;
      default:
        return false;
    }
  }
  
  
  /**
   * Compare a numeric value to a threshold with an operator.
   */
  function compareNumbers(value, operator, threshold) {
    const val = parseFloat(value);
    const thr = parseFloat(threshold);
    switch (operator) {
      case ">":
        return val > thr;
      case ">=":
        return val >= thr;
      case "<":
        return val < thr;
      case "<=":
        return val <= thr;
      case "==":
      case "eq":
        return val === thr;
      default:
        return false;
    }
  }
  
  /**
   * Compute the average for each factor. 
   *   1) Look at factor.questionIds
   *   2) Filter only scored questions (some logic to determine `hasScore`)
   *   3) Summation of user answers / count
   *   4) Return array of { factorId, avg }
   */
  function computeAllFactorAverages(questionList, answers, factors) {
    if (!Array.isArray(factors)) return [];
    
    return factors.map((f) => {
      const { factorId, questionIds } = f;
      let scoredIds = [];
      if (Array.isArray(questionIds)) {
        scoredIds = questionIds.filter((qId) => {
          const qObj = questionList.find((q) => q.uniqueId === qId);
          return qObj?.hasScore;
        });
      }
    
      let sum = 0;
      let count = 0;
      scoredIds.forEach((id) => {
        const ans = answers[id]?.response;
        if (ans !== undefined && !isNaN(ans)) {
          sum += parseFloat(ans);
          count++;
        }
      });
      const avg = count === 0 ? 0 : sum / count;
      return { factorId, avg };
    });
  }
  
  /**
   * Return the factorId of the highest-scoring factor (based on 'avg').
   */
  function getHighestFactorId(factorAverages) {
    if (!Array.isArray(factorAverages) || factorAverages.length === 0) return null;
    const sorted = [...factorAverages].sort((a, b) => b.avg - a.avg);
    return sorted[0]?.factorId || null;
  }
  
  // Uncomment and modify if you need to handle lowest scoring factor logic.
  // function getLowestFactorId(factorAverages) {
  //   if (!Array.isArray(factorAverages) || factorAverages.length === 0) return null;
  //   const sorted = [...factorAverages].sort((a, b) => a.avg - b.avg);
  //   return sorted[0]?.factorId || null;
  // }
  